/**
 * Componente de gráfico de línea para pedidos por período
 * Line chart component for orders by period
 * Diseño minimal y moderno
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { DailyMetric } from '../../models/LogisticsApiResponse';
import { PeriodTab } from './OrdersByPeriodChart';

// Colores específicos para cada estado
// Specific colors for each status
const STATUS_COLORS = {
  sent: '#296dff',      // Azul - Blue
  delivered: '#22c55e', // Verde - Green
  returned: '#ef4444',  // Rojo - Red
  in_transit: '#f97316', // Naranja - Orange
} as const;

interface OrdersPeriodLineChartProps {
  activeTab: PeriodTab;
  dailyMetrics?: DailyMetric[];
}

export const OrdersPeriodLineChart = ({
  activeTab,
  dailyMetrics,
}: OrdersPeriodLineChartProps) => {
  const { t } = useTranslation('logisticsModule');

  // Adaptar dailyMetrics a formato del gráfico
  // Adapt dailyMetrics to chart format
  const chartData = useMemo(() => {
    if (!dailyMetrics || dailyMetrics.length === 0) return [];

    return dailyMetrics.map((metric) => {
      // Formatear fecha a formato corto (DD/MM)
      // Format date to short format (DD/MM)
      const date = new Date(metric.date);
      const day = date.getDate().toString().padStart(2, '0');
      const month = date.getMonth() + 1;

      // Para el modo "all", incluir todos los valores por separado
      // For "all" mode, include all values separately
      if (activeTab === 'all') {
        return {
          date: `${day}/${month}`,
          sent: metric.orders_sent,
          delivered: metric.orders_delivered,
          in_transit: metric.orders_in_transit,
          returned: metric.orders_returned,
        };
      }

      // Para tabs individuales, solo incluir ese valor
      // For individual tabs, only include that value
      let value = 0;
      switch (activeTab) {
        case 'sent':
          value = metric.orders_sent;
          break;
        case 'delivered':
          value = metric.orders_delivered;
          break;
        case 'in_transit':
          value = metric.orders_in_transit;
          break;
        case 'returned':
          value = metric.orders_returned;
          break;
      }

      return {
        date: `${day}/${month}`,
        value,
      };
    });
  }, [dailyMetrics, activeTab]);

  // Custom tooltip minimalista para modo "all"
  // Minimalist custom tooltip for "all" mode
  const CustomTooltipAll = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const labels: Record<string, string> = {
        sent: t('orders_by_period.tabs.sent'),
        delivered: t('orders_by_period.tabs.delivered'),
        in_transit: t('orders_by_period.tabs.in_transit'),
        returned: t('orders_by_period.tabs.returned'),
      };
      return (
        <div className="bg-card-bg/95 backdrop-blur-sm border border-card-border/50 rounded-lg shadow-xl px-4 py-3">
          <p className="text-xs font-medium text-muted-foreground mb-2">{label}</p>
          <div className="space-y-1.5">
            {payload.map((entry: any, index: number) => (
              <div key={index} className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="text-xs text-muted-foreground">{labels[entry.name] || entry.name}</span>
                </div>
                <span className="text-sm font-semibold text-text">{entry.value}</span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  // Custom tooltip minimalista para tabs individuales
  // Minimalist custom tooltip for individual tabs
  const CustomTooltipSingle = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card-bg/95 backdrop-blur-sm border border-card-border/50 rounded-lg shadow-xl px-4 py-3">
          <p className="text-xs text-muted-foreground mb-1">{payload[0].payload.date}</p>
          <p className="text-lg font-semibold text-text">{payload[0].value}</p>
        </div>
      );
    }
    return null;
  };

  // Obtener color según el tab activo
  // Get color based on active tab
  const getLineColor = () => {
    switch (activeTab) {
      case 'sent':
        return STATUS_COLORS.sent;
      case 'delivered':
        return STATUS_COLORS.delivered;
      case 'in_transit':
        return STATUS_COLORS.in_transit;
      case 'returned':
        return STATUS_COLORS.returned;
      default:
        return STATUS_COLORS.sent;
    }
  };

  return (
    <div className="w-full" style={{ height: '256px' }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={chartData}
          margin={{ top: 10, right: 10, left: -10, bottom: 5 }}
        >
          {/* Grid minimalista - solo líneas horizontales sutiles */}
          <defs>
            {activeTab !== 'all' && (
              <linearGradient id={`gradient-${activeTab}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={getLineColor()} stopOpacity={0.1} />
                <stop offset="100%" stopColor={getLineColor()} stopOpacity={0} />
              </linearGradient>
            )}
            <linearGradient id="gradient-sent" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={STATUS_COLORS.sent} stopOpacity={0.1} />
              <stop offset="100%" stopColor={STATUS_COLORS.sent} stopOpacity={0} />
            </linearGradient>
          </defs>

          {/* Cuadrícula sutil - solo líneas horizontales */}
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="currentColor"
            strokeOpacity={0.08}
          />

          <XAxis
            dataKey="date"
            axisLine={false}
            tickLine={false}
            tick={{ fill: 'currentColor', fontSize: 11, opacity: 0.5 }}
            dy={10}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fill: 'currentColor', fontSize: 11, opacity: 0.5 }}
            width={35}
            tickFormatter={(value) => value.toLocaleString()}
          />

          {activeTab === 'all' ? (
            <>
              <Tooltip
                content={<CustomTooltipAll />}
                cursor={{ stroke: 'currentColor', strokeOpacity: 0.1, strokeWidth: 1 }}
              />
              <Line
                type="monotone"
                dataKey="sent"
                name="sent"
                stroke={STATUS_COLORS.sent}
                strokeWidth={2.5}
                dot={false}
                activeDot={{ r: 4, fill: STATUS_COLORS.sent, strokeWidth: 0 }}
              />
              <Line
                type="monotone"
                dataKey="delivered"
                name="delivered"
                stroke={STATUS_COLORS.delivered}
                strokeWidth={2.5}
                dot={false}
                activeDot={{ r: 4, fill: STATUS_COLORS.delivered, strokeWidth: 0 }}
              />
              <Line
                type="monotone"
                dataKey="in_transit"
                name="in_transit"
                stroke={STATUS_COLORS.in_transit}
                strokeWidth={2.5}
                dot={false}
                activeDot={{ r: 4, fill: STATUS_COLORS.in_transit, strokeWidth: 0 }}
              />
              <Line
                type="monotone"
                dataKey="returned"
                name="returned"
                stroke={STATUS_COLORS.returned}
                strokeWidth={2.5}
                dot={false}
                activeDot={{ r: 4, fill: STATUS_COLORS.returned, strokeWidth: 0 }}
              />
            </>
          ) : (
            <>
              <Tooltip
                content={<CustomTooltipSingle />}
                cursor={{ stroke: 'currentColor', strokeOpacity: 0.1, strokeWidth: 1 }}
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke={getLineColor()}
                strokeWidth={2.5}
                dot={false}
                activeDot={{ r: 5, fill: getLineColor(), strokeWidth: 2, stroke: 'white' }}
              />
            </>
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
